import { StrapiTemplate, TemplateResponseDto } from './template';

import { HsmEventStatus } from '@/constants/hsm-event-status';

export enum EventFailureReasonCodes {
  ERROR_CODE_NONE = 'none',
  ERROR_CODE_0 = '0',
  ERROR_CODE_1 = '1',
  ERROR_CODE_2 = '2',
  ERROR_CODE_4 = '4',
  ERROR_CODE_33 = '33',
  ERROR_CODE_100 = '100',
  ERROR_CODE_368 = '368',
  ERROR_CODE_408 = '408',
  ERROR_CODE_470 = '470',
  ERROR_CODE_471 = '471',
  ERROR_CODE_472 = '472',
  ERROR_CODE_503 = '503',
  ERROR_CODE_1002 = '1002',
  ERROR_CODE_1004 = '1004',
  ERROR_CODE_1005 = '1005',
  ERROR_CODE_1008 = '1008',
  ERROR_CODE_1009 = '1009',
  ERROR_CODE_1010 = '1010',
  ERROR_CODE_1011 = '1011',
  ERROR_CODE_1013 = '1013',
  ERROR_CODE_1014 = '1014',
  ERROR_CODE_1026 = '1026',
  ERROR_CODE_1031 = '1031',
  ERROR_CODE_2000 = '2000',
  ERROR_CODE_2001 = '2001',
  ERROR_CODE_2005 = '2005',
  ERROR_CODE_2008 = '2008',
  ERROR_CODE_2060 = '2060',
  ERROR_CODE_4001 = '4001',
  ERROR_CODE_4003 = '4003',
  ERROR_CODE_4005 = '4005',
  ERROR_CODE_80007 = '80007',
  ERROR_CODE_9400 = '9400',
  ERROR_CODE_9402 = '9402',
  ERROR_CODE_9403 = '9403',
  ERROR_CODE_9500 = '9500',
  ERROR_CODE_130429 = '130429',
  ERROR_CODE_130472 = '130472',
  ERROR_CODE_130497 = '130497',
  ERROR_CODE_131000 = '131000',
  ERROR_CODE_131005 = '131005',
  ERROR_CODE_131008 = '131008',
  ERROR_CODE_131009 = '131009',
  ERROR_CODE_131016 = '131016',
  ERROR_CODE_131021 = '131021',
  ERROR_CODE_131026 = '131026',
  ERROR_CODE_131031 = '131031',
  ERROR_CODE_131037 = '131037',
  ERROR_CODE_131042 = '131042',
  ERROR_CODE_131045 = '131045',
  ERROR_CODE_131047 = '131047',
  ERROR_CODE_131048 = '131048',
  ERROR_CODE_131049 = '131049',
  ERROR_CODE_131051 = '131051',
  ERROR_CODE_131052 = '131052',
  ERROR_CODE_131053 = '131053',
  ERROR_CODE_131056 = '131056',
  ERROR_CODE_131057 = '131057',
  ERROR_CODE_132000 = '132000',
  ERROR_CODE_132001 = '132001',
  ERROR_CODE_132005 = '132005',
  ERROR_CODE_132007 = '132007',
  ERROR_CODE_132012 = '132012',
  ERROR_CODE_132015 = '132015',
  ERROR_CODE_132016 = '132016',
  ERROR_CODE_132068 = '132068',
  ERROR_CODE_132069 = '132069',
  ERROR_CODE_133000 = '133000',
  ERROR_CODE_133004 = '133004',
  ERROR_CODE_133005 = '133005',
  ERROR_CODE_133006 = '133006',
  ERROR_CODE_133008 = '133008',
  ERROR_CODE_133009 = '133009',
  ERROR_CODE_133010 = '133010',
  ERROR_CODE_133015 = '133015',
  ERROR_CODE_133016 = '133016',
  ERROR_CODE_135000 = '135000',
}

export interface StrapiHsmEvent {
  id: number;
  destination: string;
  status: string;
  providerMessageId: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  hsm_campaign?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  } | null;
  renderedText: string | null;
  metaData:
    | {
        name: string;
        value: string;
      }[]
    | null;
  isTest: boolean | number | null;
  failureCode: EventFailureReasonCodes[keyof EventFailureReasonCodes] | null;
  failureReason: string | null;
  billable?: boolean;
}

export interface StrapiCampaign {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  website?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
    websiteId: string;
  } | null;
  template?: StrapiTemplate | null;
  status: string;
  contactsSource: string;
  errorMessage: string | null;
  scheduleDateTime: string | null;
  cancelledAt: string | null;
  cancellationUser: string | null;
  cancellationReason: string | null;
  contactsSourceName: string | null;
  contactsSourceLength: number | null;
}

export interface PopulatedCampaign extends StrapiCampaign {
  events: StrapiHsmEvent[];
}

/**
 * Curated WhatsApp line details returned by `GET /website/:id/hsm/line`.
 * Mirrors the backend `WhatsAppLineDetails` DTO — no secrets, no internal ids.
 */
export interface HsmLineDetails {
  provider: string | null;
  phone: string | null;
  appName: string | null;
  validateUserBlocked: boolean;
  flaggedAccount: boolean;
  mediaOverdue: boolean;
  currentAccountLimit: string | null;
}

export interface StrapiBlacklist {
  id: number;
  createdAt: string;
  updatedAt: string;
  website?: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
    websiteId: string;
  };
  blacklist_phones?: StrapiBlacklistPhone[];
}

export interface StrapiBlacklistPhone {
  id: number;
  phone: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * GOOGLE_SHEET and CONTACT_LIST MUST stay in this union even though the new
 * creation flow no longer produces them: campaigns migrated/created before the
 * contact-lists cleanup keep those values in the DB, so reading their data still
 * needs them to type-check. The BE enum keeps them for the same reason — do not
 * remove without dropping the historical rows first.
 */
export type ContactsSource =
  | 'GOOGLE_SHEET'
  | 'CONTACT_LIST'
  | 'IMPORTED_FILE'
  | 'CLIENGO_CONTACTS'
  | 'SEGMENT';

/**
 * Subset of ContactsSource supported by the new creation flow. Excludes
 * GOOGLE_SHEET and CONTACT_LIST, which only survive in ContactsSource for
 * displaying/cancelling legacy campaigns stored in the DB.
 */
export type CreationContactsSource =
  | 'IMPORTED_FILE'
  | 'CLIENGO_CONTACTS'
  | 'SEGMENT';

export type DeliveryMethod = 'SEND_NOW' | 'SCHEDULE';

/** roadmap#253: whether each send opens a fresh conversation or joins the open one. */
export type ContinuityMode = 'NEW' | 'CONTINUE';

/** roadmap#253: chatbot state applied to a continued conversation. */
export type BotState = 'ON' | 'OFF' | 'KEEP';

export enum CampaignStatus {
  SENDING = 'SENDING',
  SENT = 'SENT',
  SCHEDULED = 'SCHEDULED',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
  PREPARED = 'PREPARED',
  RESENDING = 'RESENDING',
  STOPPED = 'STOPPED',
}

/**
 * Search filters for the Cliengo contacts selection. Reserved for the filter
 * panel (Cliengo/roadmap#445); empty in the MVP.
 */
export interface ContactSearchFilters {
  search?: string;
  tags?: string;
  phaseId?: string;
  assignedTo?: string;
  lastInteractionFrom?: string;
  lastInteractionTo?: string;
}

/**
 * Option for the contacts filter selects (phases, agents, tags). `id` is the
 * value sent upstream (e.g. `phaseId`, `assignedTo`, a `tags` entry); `label`
 * is what the user sees.
 */
export interface FilterOption {
  id: string;
  label: string;
}

/** Minimal contact shape for a visible grid row (used to reconcile selection). */
export interface ContactSelectionContact {
  id: string;
  name?: string;
  phone?: string;
}

/**
 * Selection descriptor for the campaign recipients chosen from the CRM. Avoids
 * enumerating thousands of ids: `selectAll` means "every contact matching
 * `filters`" minus `excludedContactIds`; otherwise the explicit
 * `selectedContactIds` are used. The backend resolves it server-side by id
 * against the same contacts-search index, so manual picks ship only ids (no
 * inline name/phone — keeps PII out of the payload).
 */
export interface ContactSelection {
  selectAll: boolean;
  filters?: ContactSearchFilters;
  excludedContactIds: string[];
  selectedContactIds: string[];
}

/**
 * One template placeholder (`{{index}}`) mapped to a value source.
 * - `attribute`: take `contact[attribute]` (custom fields use the `leadFields.<key>`
 *   namespace); falls back to `defaultValue` when the contact's value is empty.
 * - `fixed`: the same literal value for every contact.
 *
 * A third `custom` source (a distinct value per contact) is intentionally not
 * modelled here yet — it ships with the inline/CSV editors (Cliengo/roadmap#590,
 * #591) that extend this union.
 */
export type VariableConfig =
  | {
      index: number;
      source: 'attribute';
      attribute: string;
      defaultValue: string;
    }
  | { index: number; source: 'fixed'; value: string };

/**
 * A selectable contact attribute for the variable mapping dropdown. `key` is
 * what travels in `VariableConfig.attribute` (custom fields are namespaced as
 * `leadFields.<key>`); `label` is the human-readable name; `group` splits the
 * dropdown into standard fields vs custom (lead) fields.
 */
export interface ContactAttribute {
  key: string;
  label: string;
  type: 'string' | 'number' | 'date';
  group: 'standard' | 'custom';
}

/** Stats returned by POST .../contacts/preview (mirrors the backend shape). */
export interface ContactsPreviewResponse {
  previewKey: string;
  uniqueRecipientsCount: number;
  duplicatesCount: number;
  invalidContactsCount: number;
  invalidContacts: { id: string; name?: string; phone?: string }[];
  /** Curated recipient list, rendered in the (shared) confirmation step. */
  contacts: { id: string; name?: string; phone?: string }[];
  /**
   * A few fully-resolved sample contacts (all catalog attributes, incl.
   * `leadFields`) for the variable-mapping preview pane. The full list stays
   * curated to `{ id, name, phone }`.
   */
  sampleContacts: Record<string, unknown>[];
  /** Empty-value counts per attribute key, for the live "missing" banner. */
  attributeStats: Record<string, { missingCount: number }>;
  exceedsLimit: boolean;
}

/**
 * A saved segment as the segment selector needs it. The backend enriches the
 * raw Kori definition (`{ name, filter }`) into display-ready data: a criteria
 * count and human-readable criteria labels (e.g. "Etapa = Cliente"). The
 * recipient count is NOT part of the list — it comes from the per-segment
 * preview when one is selected, to avoid counting every segment up front.
 */
export interface Segment {
  id: string;
  name: string;
  criteriaCount: number;
  criteria: { label: string }[];
}

export interface CampaignFormDTO {
  websiteId: string;
  name: string;
  template: TemplateResponseDto | null;
  contactsSource: ContactsSource;
  deliveryMethod: DeliveryMethod;
  scheduled: boolean;
  scheduleDateTime: Date | null;
  contactsFile: File | null;
  mediaFile: File | null;
  mediaUrl?: string;
  mediaType?: string;
  /** Recipient selection when contactsSource === 'CLIENGO_CONTACTS'. */
  contactSelection: ContactSelection;
  /**
   * Chosen segment when contactsSource === 'SEGMENT'. The campaign stores only
   * this reference (the backend resolves members at send time — the audience is
   * NOT frozen at create, unlike CLIENGO_CONTACTS/IMPORTED_FILE).
   */
  segmentId: string | null;
  /** Cache key of the resolved recipient list, from the last successful preview. */
  previewKey: string | null;
  /**
   * The last successful preview response, lifted to the form so the variable
   * step (which fires the preview) and the confirmation step (which renders it)
   * share one fetch — and re-fetching never clobbers the PATCH-enriched cache.
   */
  preview: ContactsPreviewResponse | null;
  /**
   * Signature of the selection that produced `preview`. When the current
   * selection's signature differs, the preview is stale and must be re-fetched.
   */
  selectionSignature: string | null;
  /** Template variable mapping ({{index}} → source). Empty when no variables. */
  templateVariables: VariableConfig[];
  /** Unset when the continuity feature flag is OFF, so nothing is sent. */
  continuityMode?: ContinuityMode;
  /** Only meaningful with continuityMode === 'CONTINUE'. */
  botState?: BotState;
}

/**
 * roadmap#253: the request can't carry a bot state with NEW (the new conversation
 * always starts with the bot ON); without a continuity mode nothing is sent.
 */
export type CampaignContinuityOptions =
  | { continuityMode?: never; botState?: never }
  | { continuityMode: 'NEW'; botState?: never }
  | { continuityMode: 'CONTINUE'; botState: BotState };

interface CreateCampaignV3BaseDTO {
  websiteId: string;
  campaignName: string;
  templateName: string;
  templateText: string;
  templateParamCount: number;
  contactsSource: ContactsSource;
  scheduled: boolean;
  scheduleDateTime: Date | null;
  mediaUrl: string | null;
  mediaType: string | null;
  /** Required when contactsSource === 'CLIENGO_CONTACTS'. */
  previewKey?: string | null;
  /** Required when contactsSource === 'SEGMENT'. */
  segmentId?: string | null;
  /**
   * Template variable mapping, sent inline only for SEGMENT. Cliengo persists
   * it via the preview PATCH (keyed by previewKey); SEGMENT has no frozen cache
   * to PATCH, so the mapping rides along here and is applied at send time.
   */
  templateVariables?: VariableConfig[];
}

export type CreateCampaignV3DTO = CreateCampaignV3BaseDTO &
  CampaignContinuityOptions;

export interface CancelCampaignFormDTO {
  websiteId: string;
  campaignId: number;
  cancellationReason: string;
}

export interface CampaignWarning {
  name: string;
  status: boolean;
  message?: string;
  [key: string]: unknown;
}

export interface Campaign {
  /** roadmap#1569 — avisos informativos no bloqueantes (ej. exceso de tier 24h). */
  warnings?: CampaignWarning[];
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  scheduleDateTime: string | null;
  eventCounter: EventCounter;
  contactsSourceLength: number;
  totalEventCounter: number;
  totalProcessedEvents: number;
  totalPendingEvents: number;
  totalSentEvents: number;
  totalUndeliveredEvents: number;
}

export type EventCounter = Record<HsmEventStatus, number>;

export interface Stats {
  from: string;
  to: string;
  internEvents: number;
  campaignEvents: number;
  notificationEvents: number;
  totalPendingEvents: number;
  totalSentEvents: number;
  totalUndeliveredEvents: number;
}

export interface FullCampaign {
  id: number;
  name: string;
  createdAt: string;
  status: string;
  scheduleDateTime: string;
  updatedAt: string;
  contactsSourceLength: number;
  contactsSource: string;
  errorMessage: string | null;
  cancelledAt: string | null;
  cancellationUser: string | null;
  cancellationReason: string | null;
  contactsSourceName: string | null;

  template: StrapiTemplate;

  eventCounter: EventCounter;
  totalEventCounter: number;
  totalProcessedEvents: number;
  /** number of events in status error with a retryable failure code (eligible for the retry button) */
  retryableErrorCount: number;

  events: StrapiHsmEvent[];
}
